import { Settings, Bell } from 'lucide-react';
import { useState } from 'react';

import { notifySuccess, notifyError } from '@/portainer/services/notifications';

import { Datatable } from '@@/datatables/Datatable';
import { ExpandableDatatable } from '@@/datatables/ExpandableDatatable';
import { TableSettingsMenu } from '@@/datatables';
import { Icon } from '@@/Icon';
import { useTableStateWithStorage } from '@@/datatables/useTableState';
import { BasicTableSettings } from '@@/datatables/types';
import { Modal } from '@@/modals';

import { createCombinedColumns, notificationChannelColumns } from './columns';
import { AlertSettingsRow } from './types';
import { AlertSettingsEditForm } from './AlertSettingsEditForm';
import {
  UseCombinedAlertSettings,
  useTestAlertManagerConnectivity,
  ConnectivityTestResponse,
} from './useAlertSettings';

interface TableSettings extends BasicTableSettings {}

export function AlertSettingsDatatable() {
  const [editingAlert, setEditingAlert] = useState<AlertSettingsRow | null>(
    null
  );
  const tableState = useTableStateWithStorage<TableSettings>(
    'alert-settings',
    'id'
  );
  const notificationChannelsTableState =
    useTableStateWithStorage<TableSettings>('notification-channels', 'id');
  const testConnectivityMutation = useTestAlertManagerConnectivity();

  function handleEditClick(alertSettings: AlertSettingsRow) {
    setEditingAlert(alertSettings);
  }

  function handleEditSave() {
    setEditingAlert(null);
  }

  function handleEditCancel() {
    setEditingAlert(null);
  }

  function handleTestClick(alertSettings: AlertSettingsRow) {
    testConnectivityMutation.mutate(alertSettings.url, {
      onSuccess: (data: ConnectivityTestResponse) => {
        if (data.reachable) {
          notifySuccess(
            'Success',
            `Alert manager "${alertSettings.name}" is reachable`
          );
        } else {
          notifyError(
            'Not Reachable',
            new Error(
              `Alert manager "${alertSettings.name}" is not reachable: ${data.message}`
            )
          );
        }
      },
      onError: () => {
        notifyError(
          'Connection Failed',
          new Error(
            `Failed to test connectivity to alert manager "${alertSettings.name}"`
          )
        );
      },
    });
  }

  const combinedColumns = createCombinedColumns(
    handleEditClick,
    handleTestClick,
    testConnectivityMutation.isLoading
  );

  function renderSubRow(row: { original: AlertSettingsRow }) {
    const alertManager = row.original;
    const notificationChannels = alertManager.notificationChannels || [];
    const channelCount = notificationChannels.length;

    return (
      <tr className="hover:!bg-inherit">
        <td />
        <td colSpan={Number.MAX_SAFE_INTEGER}>
          <div className="border-t border-gray-200">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                <Icon icon={Bell} className="w-4 h-4" />
                Notification Channels ({channelCount})
              </h4>
            </div>

            {notificationChannels.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                No notification channels configured
              </div>
            ) : (
              <Datatable
                dataset={notificationChannels}
                columns={notificationChannelColumns}
                settingsManager={notificationChannelsTableState}
                disableSelect
                noWidget
                emptyContentLabel="No notification channels found"
                data-cy={`notification-channels-${alertManager.id}`}
              />
            )}
          </div>
        </td>
      </tr>
    );
  }

  const alertSettings = UseCombinedAlertSettings();

  return (
    <>
      <ExpandableDatatable
        title="Alert Settings"
        titleIcon={Settings}
        dataset={alertSettings.data || []}
        settingsManager={tableState}
        columns={combinedColumns}
        getRowCanExpand={(row) =>
          (row.original.notificationChannels || []).length > 0
        }
        renderSubRow={renderSubRow}
        disableSelect={false}
        isRowSelectable={(row) =>
          !(row.original.id === '1' && row.original.isInternal)
        }
        renderTableSettings={() => (
          <TableSettingsMenu>
            <div className="text-sm text-gray-500">Settings menu</div>
          </TableSettingsMenu>
        )}
        data-cy="alert-settings-datatable"
      />

      {/* Edit Modal */}
      {editingAlert && (
        <Modal onDismiss={handleEditCancel} size="xl">
          <AlertSettingsEditForm
            alertSettings={editingAlert}
            onSave={handleEditSave}
            onCancel={handleEditCancel}
          />
        </Modal>
      )}
    </>
  );
}
